/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Mon, Aug 16th 2021, 3:25:12 pm
 *
 * *****************************************************
 */

import { useState, useCallback, useRef, useEffect, createContext } from 'react';

declare global {
  interface Window {
    captchaOnLoad: () => void;
    grecaptcha: ReCaptchaInstance;
  }
}

type ReCaptchaInstance = {
  ready: (cb: () => any) => any;
  execute: (clientId: string, options: ReCaptchaExecuteOptions) => Promise<string>;
  render: (id: string, options: ReCaptchaRenderOptions) => any;
};

type ReCaptchaExecuteOptions = {
  action: string;
};

type ReCaptchaRenderOptions = {
  sitekey: string;
  size: 'invisible';
  badge: 'bottomright' | 'bottomleft' | 'inline';
};

type Props = {
  children: React.ReactNode;
};

type CaptchaProps = {
  isReady: boolean;
  execute: (action: string) => Promise<string>;
};

export const GoogleReCaptchaV3ProviderContext = createContext<CaptchaProps>({
  isReady: false,
  execute: () => Promise.resolve('')
});
const GoogleReCaptchaV3Provider = ({ children }: Props): JSX.Element => {
  const [isReady, setIsReady] = useState(false);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const clientID = useRef<string>('');

  const onLoad = useCallback(async () => {
    const widgetElement = document.createElement('div');
    widgetElement.id = 'g-recaptcha';

    widgetRef.current = document.body.appendChild(widgetElement);

    const returnedClientID = await window.grecaptcha.render('g-recaptcha', {
      sitekey: process.env.REACT_APP_GOOGLE_RECAPTCHA_CLIENT_SITE_KEY!,
      size: 'invisible',
      badge: 'bottomleft'
    });

    clientID.current = returnedClientID;

    window.grecaptcha.ready(() => {
      setIsReady(true);
    });
  }, []);

  const loadScript = useCallback(() => {
    window.captchaOnLoad = onLoad;

    const url = `https://www.google.com/recaptcha/api.js`;
    const queryString = '?onload=captchaOnLoad&render=explicit';
    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.src = url + queryString;
    scriptElement.async = true;
    scriptElement.defer = true;

    scriptRef.current = document.body.appendChild(scriptElement);
  }, [onLoad]);

  const executeCaptcha = useCallback(
    action => {
      if (!isReady) throw new Error('Execute recaptcha not yet available. Please try again!');
      return window.grecaptcha.execute(clientID.current, { action });
    },
    [isReady]
  );

  useEffect(() => {
    loadScript();
    return () => {
      if (widgetRef.current) document.removeChild(widgetRef.current);
      if (scriptRef.current) document.removeChild(scriptRef.current);
    };
  }, [loadScript]);

  return (
    <GoogleReCaptchaV3ProviderContext.Provider
      value={{
        isReady,
        execute: executeCaptcha
      }}>
      {children}
    </GoogleReCaptchaV3ProviderContext.Provider>
  );
};

export default GoogleReCaptchaV3Provider;
